// src/pages/TimetablePage.jsx
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// ─────────────────────────────────────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
import { X, AlertTriangle, Ban, Trash2, Pencil, ArrowLeft, ClipboardList, BookOpen, Printer, Lightbulb, Square, BarChart3 } from "lucide-react";
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DEFAULT_PERIODS = [{
  id: 1,
  label: "Period 1",
  time: "8:30 – 9:10",
  duration: 40,
  isBreak: false
}, {
  id: 2,
  label: "Period 2",
  time: "9:10 – 9:50",
  duration: 40,
  isBreak: false
}, {
  id: 3,
  label: "Period 3",
  time: "9:50 – 10:30",
  duration: 40,
  isBreak: false
}, {
  id: 4,
  label: "Break",
  time: "10:30 – 10:45",
  duration: 15,
  isBreak: true
}, {
  id: 5,
  label: "Period 4",
  time: "10:45 – 11:25",
  duration: 40,
  isBreak: false
}, {
  id: 6,
  label: "Period 5",
  time: "11:25 – 12:05",
  duration: 40,
  isBreak: false
}, {
  id: 7,
  label: "Lunch",
  time: "12:05 – 12:45",
  duration: 40,
  isBreak: true
}, {
  id: 8,
  label: "Period 6",
  time: "12:45 – 1:25",
  duration: 40,
  isBreak: false
}, {
  id: 9,
  label: "Period 7",
  time: "1:25 – 2:05",
  duration: 40,
  isBreak: false
}, {
  id: 10,
  label: "Period 8",
  time: "2:05 – 2:45",
  duration: 40,
  isBreak: false
}];
const REGULAR_STANDARDS = ["LKG", "UKG", ...Array.from({
  length: 10
}, (_, i) => `Class ${i + 1}`)];
const REGULAR_SECTIONS = ["A", "B", "C"];
const HS_STANDARDS = ["Class 11", "Class 12"];
const HS_GROUPS = ["Bio Maths", "Computer Science", "Art & Science", "Commerce"];
const HS_SECTIONS = ["A", "B"];
const HS_GROUP_COLORS = {
  "Bio Maths": {
    bg: "#dbeafe",
    color: "#1e40af",
    border: "#93c5fd",
    dot: "#3b82f6"
  },
  "Computer Science": {
    bg: "#dbeafe",
    color: "#1e40af",
    border: "#93c5fd",
    dot: "#3b82f6"
  },
  "Art & Science": {
    bg: "#dbeafe",
    color: "#1e40af",
    border: "#93c5fd",
    dot: "#3b82f6"
  },
  "Commerce": {
    bg: "#dbeafe",
    color: "#1e40af",
    border: "#93c5fd",
    dot: "#3b82f6"
  }
};
const LKG_SUBJECTS = ["English", "Tamil", "Mathematics", "EVS (Environmental Studies)", "Body Parts", "Drawing / Coloring", "Rhymes & Storytelling", "Activity / Play Time", "GK (General Knowledge)", "Dance / Music / Yoga", "Handwriting Practice"];
const UKG_SUBJECTS = ["English", "Tamil", "Mathematics", "EVS (Environmental Studies)", "GK (General Knowledge)", "Handwriting", "Drawing / Coloring / Craft", "Rhymes & Storytelling", "Music / Dance / Yoga / Games", "Computer Basics"];
const CORE_SUBJECTS = ["Tamil", "English", "Mathematics", "Science", "Social Science", "Computer", "Physical Education", "Art & Craft", "Moral Science", "Library", "Sports", "Value Education"];
const HS_SUBJECTS = {
  "Bio Maths": ["Tamil", "English", "Mathematics", "Physics", "Chemistry", "Biology", "Computer Science", "Physical Education"],
  "Computer Science": ["Tamil", "English", "Mathematics", "Physics", "Chemistry", "Computer Science", "Commerce", "Physical Education"],
  "Art & Science": ["Tamil", "English", "History", "Geography", "Economics", "Political Science", "Sociology", "French", "Physical Education"],
  "Commerce": ["Tamil", "English", "Accountancy", "Business Maths", "Economics", "Commerce", "Computer Applications", "Physical Education"]
};
const COMMON_EXTRAS = ["Free Period", "Assembly", "Games", "Sports"];

// ─────────────────────────────────────────────────────────────────────────────
//  SUBJECT COLOR MAP
// ─────────────────────────────────────────────────────────────────────────────
const SUBJ_COLORS = {
  "Tamil": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "English": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Mathematics": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Science": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Social Science": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Computer": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Computer Science": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Computer Basics": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Computer Applications": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Physics": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Chemistry": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Biology": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "History": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Geography": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Economics": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Commerce": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Accountancy": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Business Maths": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Political Science": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Sociology": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "French": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Physical Education": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Art & Craft": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Moral Science": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Library": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Sports": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Value Education": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Free Period": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Assembly": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Games": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "EVS (Environmental Studies)": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Body Parts": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Drawing / Coloring": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Drawing / Coloring / Craft": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Rhymes & Storytelling": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Activity / Play Time": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "GK (General Knowledge)": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Dance / Music / Yoga": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Music / Dance / Yoga / Games": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Handwriting Practice": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  },
  "Handwriting": {
    bg: "#E3F2FD",
    color: "#0D47A1"
  }
};
const getCellColor = s => SUBJ_COLORS[s] || {
  bg: "#f8fafc",
  color: "#374151"
};

// ─────────────────────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function emptyTimetable(periods) {
  const tt = {};
  DAYS.forEach(day => {
    tt[day] = {};
    periods.forEach(p => {
      if (!p.isBreak) tt[day][p.id] = {
        subject: "",
        teacher: "",
        duration: p.duration
      };
    });
  });
  return tt;
}
function buildSubjectOptions(classInfo) {
  if (classInfo.std === "LKG") return [...LKG_SUBJECTS, ...COMMON_EXTRAS];
  if (classInfo.std === "UKG") return [...UKG_SUBJECTS, ...COMMON_EXTRAS];
  if (classInfo.type === "hs") return [...(HS_SUBJECTS[classInfo.grp] || CORE_SUBJECTS), ...COMMON_EXTRAS];
  return [...CORE_SUBJECTS, ...COMMON_EXTRAS];
}

// Filter staff list down to only the staff who teach a given subject.
// Matches against each staff member's `subjects` array first, then falls
// back to their `dept` field.
//
// ⚠ FIX: Staff Management stores short department names ("Maths",
// "Social", ...) while the Timetable subject list uses full subject
// names ("Mathematics", "Social Science", ...). A plain `dept === subject`
// check only ever matched subjects that happen to be spelled identically
// to their department (Tamil, English, Science) — "Mathematics" never
// matched staff whose dept is "Maths", even though those ARE the maths
// teachers. This adds:
//   1. An explicit alias map for the known short-name departments, and
//   2. A loose two-way substring fallback (handles "Computer" subject vs
//      "Computer Science" dept, "Social Science" subject vs "Social"
//      dept, etc.) so any future subject/department naming still matches.
const SUBJECT_TO_DEPT_ALIASES = {
  "mathematics": ["maths"],
  "social science": ["social"],
  "computer": ["computer science"],
  "computer applications": ["computer science", "commerce"],
  "computer basics": ["computer science"],
  "physical education": ["sports"],
  "business maths": ["commerce"]
};
function getStaffForSubject(staffList, subject) {
  const subjTrimmed = (subject || "").trim().toLowerCase();
  if (!subjTrimmed) return [];
  const acceptableDepts = new Set([subjTrimmed, ...(SUBJECT_TO_DEPT_ALIASES[subjTrimmed] || [])]);
  return staffList.filter(s => {
    const subjArr = Array.isArray(s.subjects) ? s.subjects.map(x => (x || "").toLowerCase().trim()) : [];
    const dept = (s.dept || "").toLowerCase().trim();
    if (!dept && subjArr.length === 0) return false;
    if (subjArr.includes(subjTrimmed)) return true;
    if (dept && acceptableDepts.has(dept)) return true;
    // Loose fallback: either string contains the other (e.g. "computer
    // science" dept contains "computer" subject, "social science" subject
    // contains "social" dept).
    if (dept && (dept.includes(subjTrimmed) || subjTrimmed.includes(dept))) return true;
    return false;
  });
}

// Responsive hook
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ─────────────────────────────────────────────────────────────────────────────
//  TEACHER ALLOCATION CHECK
// ─────────────────────────────────────────────────────────────────────────────
function isTeacherAllocated(teacher, day, periodId, allTimetables, currentKey) {
  if (!teacher || !teacher.trim()) return false;
  const teacherTrimmed = teacher.trim().toLowerCase();
  for (const [className, classData] of Object.entries(allTimetables)) {
    if (className === currentKey) continue;
    const timetable = classData.timetable || {};
    const dayData = timetable[day] || {};
    const cell = dayData[periodId];
    if (cell && cell.teacher && cell.teacher.trim().toLowerCase() === teacherTrimmed) {
      return true;
    }
  }
  return false;
}

// Returns the class name where `teacher` is already allocated on this
// day + period (excluding `currentKey`), or null if the teacher is free.
function findTeacherConflictClass(teacher, day, periodId, allTimetables, currentKey) {
  if (!teacher || !teacher.trim()) return null;
  const teacherTrimmed = teacher.trim().toLowerCase();
  for (const [className, classData] of Object.entries(allTimetables)) {
    if (className === currentKey) continue;
    const timetable = classData.timetable || {};
    const dayData = timetable[day] || {};
    const cell = dayData[periodId];
    if (cell && cell.teacher && cell.teacher.trim().toLowerCase() === teacherTrimmed) {
      return className;
    }
  }
  return null;
}

// Scans an entire class's timetable (all days / all periods) and returns
// a list of every teacher-clash found against other classes. Used as a
// final safety-net check right before the whole timetable is saved, so a
// conflict can never slip through even if it wasn't caught cell-by-cell.
function findAllConflicts(timetable, allTimetables, currentKey) {
  const conflicts = [];
  Object.entries(timetable || {}).forEach(([day, dayObj]) => {
    Object.entries(dayObj || {}).forEach(([periodId, cell]) => {
      if (!cell || !cell.teacher || !cell.teacher.trim()) return;
      const conflictClass = findTeacherConflictClass(cell.teacher, day, periodId, allTimetables, currentKey);
      if (conflictClass) {
        conflicts.push({
          day,
          periodId,
          teacher: cell.teacher.trim(),
          conflictClass
        });
      }
    });
  });
  return conflicts;
}

// ─────────────────────────────────────────────────────────────────────────────
//  PERIOD HEADER EDITOR MODAL
// ─────────────────────────────────────────────────────────────────────────────
function PeriodHeaderEditor({
  period,
  onSave,
  onClose
}) {
  const [label, setLabel] = useState(period.label);
  const [time, setTime] = useState(period.time);
  const [duration, setDuration] = useState(String(period.duration));
  const [isBreak, setIsBreak] = useState(!!period.isBreak);
  const handleSave = () => {
    const dur = parseInt(duration, 10);
    onSave({
      ...period,
      label: label.trim() || period.label,
      time: time.trim(),
      duration: isNaN(dur) || dur <= 0 ? period.duration : dur,
      isBreak
    });
  };
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.65)",
    zIndex: 4000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(5px)"
  }} onClick={onClose}>
      <div style={{
      background: "#fff",
      borderRadius: 18,
      width: "min(400px,94%)",
      boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
      overflow: "hidden"
    }} onClick={e => e.stopPropagation()}>
        <div style={{
        background: "linear-gradient(135deg,#0D47A1,#2196F3)",
        padding: "16px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: "#fff"
          }}>Edit Column Header</div>
            <div style={{
            fontSize: 11,
            color: "#90CAF9",
            marginTop: 3
          }}>Edit period label, time range & duration</div>
          </div>
          <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.12)",
          border: "none",
          color: "#fff",
          width: 32,
          height: 32,
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 16
        }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
        </div>

        <div style={{
        padding: "20px"
      }}>
          <div style={{
          marginBottom: 14
        }}>
            <label style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#64748b",
            display: "block",
            marginBottom: 5,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
              Period Label
            </label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Period 1, Break, Lunch" style={{
            width: "100%",
            padding: "9px 12px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            fontSize: 13,
            fontFamily: "'Poppins',sans-serif",
            outline: "none",
            background: "#f8fafc",
            boxSizing: "border-box"
          }} />
          </div>

          <div style={{
          marginBottom: 14
        }}>
            <label style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#64748b",
            display: "block",
            marginBottom: 5,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
              Time Range
            </label>
            <input value={time} onChange={e => setTime(e.target.value)} placeholder="e.g. 8:30 – 9:10" style={{
            width: "100%",
            padding: "9px 12px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            fontSize: 13,
            fontFamily: "'Poppins',sans-serif",
            outline: "none",
            background: "#f8fafc",
            boxSizing: "border-box"
          }} />
          </div>

          <div style={{
          marginBottom: 14
        }}>
            <label style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#64748b",
            display: "block",
            marginBottom: 5,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
              Duration (minutes)
            </label>
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap"
          }}>
              <input type="number" min={5} max={180} value={duration} onChange={e => setDuration(e.target.value)} style={{
              width: 80,
              padding: "9px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 13,
              fontFamily: "'Poppins',sans-serif",
              outline: "none",
              background: "#f8fafc"
            }} />
              <div style={{
              display: "flex",
              gap: 4
            }}>
                {[15, 30, 40, 45, 60].map(m => <button key={m} onClick={() => setDuration(String(m))} style={{
                padding: "4px 8px",
                borderRadius: 6,
                border: "1px solid #e2e8f0",
                background: duration === String(m) ? "#0D47A1" : "#f1f5f9",
                color: duration === String(m) ? "#fff" : "#64748b",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer"
              }}>{m}</button>)}
              </div>
            </div>
          </div>

          <div style={{
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 14px",
          background: "#f8fafc",
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          cursor: "pointer"
        }} onClick={() => setIsBreak(b => !b)}>
            <div style={{
            flex: 1
          }}>
              <div style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#1e293b"
            }}>Break / Lunch Column</div>
              <div style={{
              fontSize: 10,
              color: "#94a3b8",
              marginTop: 2
            }}>
                When enabled, subject cells will not be shown
              </div>
            </div>
            <div style={{
            width: 42,
            height: 24,
            borderRadius: 12,
            cursor: "pointer",
            transition: "all 0.2s",
            background: isBreak ? "#2196F3" : "#e2e8f0",
            position: "relative",
            flexShrink: 0
          }}>
              <div style={{
              position: "absolute",
              top: 3,
              left: isBreak ? 21 : 3,
              width: 18,
              height: 18,
              borderRadius: 9,
              background: "#fff",
              transition: "left 0.2s"
            }} />
            </div>
          </div>

          <div style={{
          display: "flex",
          gap: 8
        }}>
            <button onClick={onClose} style={{
            flex: 1,
            background: "#f1f5f9",
            color: "#64748b",
            border: "none",
            padding: "10px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer"
          }}>
              Cancel
            </button>
            <button onClick={handleSave} style={{
            flex: 2,
            background: "linear-gradient(135deg,#0D47A1,#2196F3)",
            color: "#fff",
            border: "none",
            padding: "10px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer"
          }}>
              Update Header
            </button>
          </div>
        </div>
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  CELL EDITOR MODAL
// ─────────────────────────────────────────────────────────────────────────────
function CellEditor({
  day,
  period,
  value,
  subjects,
  staffList,
  allTimetables,
  currentKey,
  onSave,
  onDelete,
  onClose
}) {
  const [subject, setSubject] = useState(value?.subject || "");
  const [teacher, setTeacher] = useState(value?.teacher || "");
  const [duration, setDuration] = useState(value?.duration != null ? String(value.duration) : String(period.duration || 40));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [conflictClass, setConflictClass] = useState(null);
  const clr = getCellColor(subject);

  // Only staff whose `subjects` (or `dept`) matches the currently
  // selected subject show up in the teacher dropdown.
  const matchedStaff = getStaffForSubject(staffList, subject);
  const teacherNameOptions = matchedStaff.map(s => s.name);

  // For every subject-matched teacher, work out if they are already
  // allocated to another class on this exact day + period, so we can
  // warn the admin *before* they even pick a name (not just after).
  const busyTeachers = matchedStaff.map(s => ({
    name: s.name,
    conflictClass: findTeacherConflictClass(s.name, day, period.id, allTimetables, currentKey)
  })).filter(t => t.conflictClass);
  useEffect(() => {
    setConflictClass(findTeacherConflictClass(teacher, day, period.id, allTimetables, currentKey));
  }, [teacher, day, period.id, allTimetables, currentKey]);
  const hasConflict = conflictClass !== null;

  // If the subject changes and the currently typed teacher no longer
  // teaches that subject, clear it so a mismatched name isn't kept.
  const handleSubjectChange = val => {
    setSubject(val);
    const stillValid = getStaffForSubject(staffList, val).some(s => s.name.toLowerCase() === teacher.trim().toLowerCase());
    if (teacher && !stillValid) setTeacher("");
  };
  const handleSave = () => {
    if (hasConflict) {
      return;
    }
    const dur = parseInt(duration, 10);
    onSave({
      subject: subject.trim(),
      teacher: teacher.trim(),
      duration: isNaN(dur) || dur <= 0 ? period.duration || 40 : dur
    });
  };
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.65)",
    zIndex: 3000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(5px)"
  }} onClick={onClose}>
      <div style={{
      background: "#fff",
      borderRadius: 18,
      width: "min(420px,94%)",
      boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
      overflow: "hidden"
    }} onClick={e => e.stopPropagation()}>
        <div style={{
        background: "linear-gradient(135deg,#0D47A1,#2196F3)",
        padding: "18px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: "#fff"
          }}>Edit Period</div>
            <div style={{
            fontSize: 11,
            color: "#90CAF9",
            marginTop: 3
          }}>
              {day} · {period.label} · {period.time}
            </div>
          </div>
          <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.12)",
          border: "none",
          color: "#fff",
          width: 32,
          height: 32,
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 16
        }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
        </div>

        <div style={{
        padding: "20px"
      }}>
          <div style={{
          marginBottom: 14
        }}>
            <label style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#64748b",
            display: "block",
            marginBottom: 5,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>Subject</label>
            <input list="subject-options" value={subject} onChange={e => handleSubjectChange(e.target.value)} placeholder="Select or type a subject..." autoComplete="off" style={{
            width: "100%",
            padding: "9px 12px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            fontSize: 13,
            fontFamily: "'Poppins',sans-serif",
            outline: "none",
            background: "#f8fafc",
            boxSizing: "border-box"
          }} />
            <datalist id="subject-options">
              {subjects.map(s => <option key={s} value={s} />)}
            </datalist>
            {subject && <div style={{
            marginTop: 6,
            display: "inline-block",
            padding: "3px 10px",
            borderRadius: 5,
            fontSize: 11,
            fontWeight: 600,
            background: clr.bg,
            color: clr.color
          }}>{subject}</div>}
          </div>

          <div style={{
          marginBottom: 14
        }}>
            <label style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#64748b",
            display: "block",
            marginBottom: 5,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
              Teacher Name
              {subject.trim() && <span style={{
              textTransform: "none",
              fontWeight: 400,
              color: "#94a3b8",
              letterSpacing: 0
            }}>
                  {" "}— {subject.trim()} teachers only
                </span>}
            </label>
            <input list="teacher-options" value={teacher} onChange={e => setTeacher(e.target.value)} placeholder={subject.trim() ? "Select or type a teacher..." : "Select a subject first..."} autoComplete="off" disabled={!subject.trim()} style={{
            width: "100%",
            padding: "9px 12px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            fontSize: 13,
            fontFamily: "'Poppins',sans-serif",
            outline: "none",
            background: subject.trim() ? "#f8fafc" : "#f1f5f9",
            boxSizing: "border-box",
            cursor: subject.trim() ? "text" : "not-allowed"
          }} />
            <datalist id="teacher-options">
              {teacherNameOptions.map(t => <option key={t} value={t} />)}
            </datalist>
            {!subject.trim() && <div style={{
            marginTop: 5,
            fontSize: 10,
            color: "#94a3b8"
          }}>
                Select a subject above to see the staff who teach it.
              </div>}
            {subject.trim() && teacherNameOptions.length === 0 && <div style={{
            marginTop: 5,
            fontSize: 10,
            color: "#94a3b8"
          }}>
                No staff found for "{subject.trim()}" — check backend / Staff records.
              </div>}
            {/* Proactive heads-up: shows which of this subject's teachers are
                already booked elsewhere for this exact day + period, before
                the admin even picks one. */}
            {subject.trim() && busyTeachers.length > 0 && <div style={{
            marginTop: 6,
            padding: "7px 10px",
            background: "#fff7ed",
            border: "1px solid #fdba74",
            borderRadius: 7
          }}>
                <div style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#9a3412",
              marginBottom: 2
            }}>
                  <AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> Already busy this period:
                </div>
                {busyTeachers.map(t => <div key={t.name} style={{
              fontSize: 10,
              color: "#9a3412"
            }}>
                    {t.name} — {t.conflictClass}
                  </div>)}
              </div>}
          </div>

          {hasConflict && <div style={{
          marginBottom: 14,
          padding: "12px 14px",
          background: "#fee2e2",
          border: "1px solid #dc2626",
          borderRadius: 8
        }}>
              <div style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 4
          }}>
                <span style={{
              fontSize: 16
            }}><Ban className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                <span style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#991b1b"
            }}>
                  Teacher Already Allocated!
                </span>
              </div>
              <div style={{
            fontSize: 11,
            color: "#7f1d1d"
          }}>
                <strong>{teacher.trim()}</strong> is already assigned to <strong>{conflictClass}</strong> for this day and period.
              </div>
              <div style={{
            fontSize: 10,
            color: "#7f1d1d",
            marginTop: 4,
            fontWeight: 500
          }}>
                Please select a different teacher or period.
              </div>
            </div>}

          <div style={{
          marginBottom: 20
        }}>
            <label style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#64748b",
            display: "block",
            marginBottom: 5,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
              Duration (minutes)
            </label>
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap"
          }}>
              <input type="number" min={5} max={180} value={duration} onChange={e => setDuration(e.target.value)} style={{
              width: 80,
              padding: "9px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 13,
              fontFamily: "'Poppins',sans-serif",
              outline: "none",
              background: "#f8fafc"
            }} />
              <div style={{
              display: "flex",
              gap: 4
            }}>
                {[30, 40, 45, 60].map(m => <button key={m} onClick={() => setDuration(String(m))} style={{
                padding: "4px 8px",
                borderRadius: 6,
                border: "1px solid #e2e8f0",
                background: duration === String(m) ? "#0D47A1" : "#f1f5f9",
                color: duration === String(m) ? "#fff" : "#64748b",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer"
              }}>{m}</button>)}
              </div>
            </div>
          </div>

          {confirmDelete ? <div style={{
          background: "#fff5f5",
          border: "1px solid #fecaca",
          borderRadius: 10,
          padding: "14px"
        }}>
              <div style={{
            fontSize: 12,
            color: "#991b1b",
            fontWeight: 600,
            marginBottom: 10,
            textAlign: "center"
          }}>
                Delete this period entry?
              </div>
              <div style={{
            display: "flex",
            gap: 8
          }}>
                <button onClick={() => setConfirmDelete(false)} style={{
              flex: 1,
              background: "#f1f5f9",
              color: "#64748b",
              border: "none",
              padding: "9px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer"
            }}>Cancel</button>
                <button onClick={onDelete} style={{
              flex: 1,
              background: "#dc2626",
              color: "#fff",
              border: "none",
              padding: "9px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer"
            }}>Yes, Delete</button>
              </div>
            </div> : <div style={{
          display: "flex",
          gap: 8
        }}>
              <button onClick={() => setConfirmDelete(true)} style={{
            flex: 1,
            background: "#fee2e2",
            color: "#991b1b",
            border: "1px solid #fecaca",
            padding: "9px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer"
          }}>Delete</button>
              <button onClick={onClose} style={{
            flex: 1,
            background: "#f1f5f9",
            color: "#64748b",
            border: "none",
            padding: "9px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer"
          }}>Cancel</button>
              <button onClick={handleSave} disabled={hasConflict} style={{
            flex: 2,
            background: hasConflict ? "#d1d5db" : "#0D47A1",
            color: hasConflict ? "#9ca3af" : "#fff",
            border: "none",
            padding: "9px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            cursor: hasConflict ? "not-allowed" : "pointer"
          }}>
                Save
              </button>
            </div>}
        </div>
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  CONFIRM DIALOG
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmDialog({
  message,
  onConfirm,
  onCancel
}) {
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.75)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(4px)"
  }}>
      <div style={{
      background: "#fff",
      borderRadius: 16,
      width: "min(380px,90%)",
      padding: "24px",
      textAlign: "center",
      boxShadow: "0 20px 35px rgba(0,0,0,0.2)"
    }}>
        <div style={{
        fontSize: 28,
        marginBottom: 12
      }}><Trash2 className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
        <div style={{
        fontSize: 14,
        color: "#0f172a",
        marginBottom: 24,
        lineHeight: 1.6
      }}>{message}</div>
        <div style={{
        display: "flex",
        gap: 10
      }}>
          <button onClick={onCancel} style={{
          flex: 1,
          background: "#f1f5f9",
          color: "#64748b",
          border: "none",
          padding: "10px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer"
        }}>Cancel</button>
          <button onClick={onConfirm} style={{
          flex: 1,
          background: "#dc2626",
          color: "#fff",
          border: "none",
          padding: "10px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer"
        }}>Delete</button>
        </div>
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  TIMETABLE GRID
// ─────────────────────────────────────────────────────────────────────────────
function TimetableGrid({
  classInfo,
  timetable,
  periods,
  staffList,
  allTimetables,
  currentKey,
  onCellSave,
  onCellDelete,
  onPeriodHeaderSave
}) {
  const [editingCell, setEditingCell] = useState(null);
  const [editingHeader, setEditingHeader] = useState(null);
  const subjects = buildSubjectOptions(classInfo);
  return <>
      <div style={{
      overflowX: "auto",
      WebkitOverflowScrolling: "touch"
    }}>
        <table style={{
        width: "100%",
        borderCollapse: "collapse",
        minWidth: 600
      }}>
          <thead>
            <tr>
              <th style={{
              padding: "10px 10px",
              background: "#0D47A1",
              color: "#FFFFFF",
              fontSize: 10,
              fontWeight: 600,
              textAlign: "left",
              borderRight: "1px solid #2196F3",
              width: 80,
              whiteSpace: "nowrap"
            }}>
                Day
              </th>
              {periods.map(p => <th key={p.id} onClick={() => setEditingHeader(p)} title="Click to edit" style={{
              padding: "0",
              background: p.isBreak ? "#2196F3" : "#0D47A1",
              borderRight: "1px solid #2196F3",
              minWidth: p.isBreak ? 50 : 72,
              cursor: "pointer",
              verticalAlign: "middle"
            }}>
                  <div style={{
                padding: "6px 4px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2
              }}>
                    <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3
                }}>
                      <span style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: p.isBreak ? "#FFFFFF" : "#FFFFFF"
                  }}>
                        {p.label}
                      </span>
                      <span style={{
                    fontSize: 7,
                    color: p.isBreak ? "#FFFFFF" : "#FFFFFF"
                  }}><Pencil className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                    </div>
                    <div style={{
                  fontSize: 8,
                  color: p.isBreak ? "#FFFFFF" : "#FFFFFF"
                }}>{p.time}</div>
                    <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 2,
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: 4,
                  padding: "1px 5px",
                  marginTop: 1
                }}>
                      <span style={{
                    fontSize: 7,
                    color: "#FFFFFF"
                  }}>⏱</span>
                      <span style={{
                    fontSize: 7,
                    fontWeight: 700,
                    color: p.isBreak ? "#FFFFFF" : "#FFFFFF"
                  }}>
                        {p.duration}m
                      </span>
                    </div>
                  </div>
                </th>)}
            </tr>
          </thead>

          <tbody>
            {DAYS.map((day, di) => <tr key={day} style={{
            background: di % 2 === 0 ? "#fff" : "#fafafa"
          }}>
                <td style={{
              padding: "6px 8px",
              fontSize: 10,
              fontWeight: 700,
              color: "#0D47A1",
              background: di % 2 === 0 ? "#E3F2FD" : "#f1f5f9",
              borderRight: "1px solid #e2e8f0",
              borderBottom: "1px solid #f1f5f9",
              whiteSpace: "nowrap"
            }}>
                  {day.slice(0, 3)}
                </td>
                {periods.map(p => {
              if (p.isBreak) return <td key={p.id} style={{
                textAlign: "center",
                fontSize: 9,
                color: "#94a3b8",
                fontStyle: "italic",
                background: "#fafafa",
                borderRight: "1px solid #f1f5f9",
                borderBottom: "1px solid #f1f5f9",
                padding: 4
              }}>
                      {p.label.slice(0, 3)}
                    </td>;
              const cell = timetable?.[day]?.[p.id] || {
                subject: "",
                teacher: "",
                duration: p.duration
              };
              const clr = getCellColor(cell.subject);
              const isEmpty = !cell.subject;
              const dur = cell.duration || p.duration;
              return <td key={p.id} style={{
                padding: 3,
                borderRight: "1px solid #f1f5f9",
                borderBottom: "1px solid #f1f5f9",
                position: "relative"
              }}>
                      <div onClick={() => setEditingCell({
                  day,
                  period: p
                })} title="Click to edit" style={{
                  padding: "5px 6px",
                  borderRadius: 6,
                  cursor: "pointer",
                  background: isEmpty ? "#f8fafc" : clr.bg,
                  border: isEmpty ? "1px dashed #e2e8f0" : `1px solid ${clr.bg}`,
                  minHeight: 52,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  transition: "all 0.15s"
                }}>
                        {isEmpty ? <span style={{
                    fontSize: 9,
                    color: "#cbd5e1",
                    textAlign: "center"
                  }}>+ Add</span> : <>
                            <div style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: clr.color,
                      lineHeight: 1.3
                    }}>{cell.subject}</div>
                            {cell.teacher && <div style={{
                      fontSize: 8,
                      color: clr.color,
                      opacity: 0.7,
                      marginTop: 2
                    }}>{cell.teacher}</div>}
                            <div style={{
                      marginTop: 3,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      background: "rgba(255,255,255,0.7)",
                      borderRadius: 4,
                      padding: "1px 4px",
                      fontSize: 7,
                      fontWeight: 700,
                      color: clr.color,
                      alignSelf: "flex-start"
                    }}>
                              ⏱ {dur}m
                            </div>
                          </>}
                      </div>
                      {!isEmpty && <button title="Delete this entry" onClick={e => {
                  e.stopPropagation();
                  onCellDelete(day, p.id);
                }} className="cell-del-btn" style={{
                  position: "absolute",
                  top: 5,
                  right: 5,
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  background: "#fee2e2",
                  color: "#dc2626",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 8,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0,
                  transition: "opacity 0.15s"
                }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>

      {editingCell && <CellEditor day={editingCell.day} period={editingCell.period} value={timetable?.[editingCell.day]?.[editingCell.period.id]} subjects={subjects} staffList={staffList} allTimetables={allTimetables} currentKey={currentKey} onSave={val => {
      onCellSave(editingCell.day, editingCell.period.id, val);
      setEditingCell(null);
    }} onDelete={() => {
      onCellDelete(editingCell.day, editingCell.period.id);
      setEditingCell(null);
    }} onClose={() => setEditingCell(null)} />}

      {editingHeader && <PeriodHeaderEditor period={editingHeader} onSave={updated => {
      onPeriodHeaderSave(updated);
      setEditingHeader(null);
    }} onClose={() => setEditingHeader(null)} />}
    </>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const API = "https://schoolcrm-fzcv.onrender.com/api/timetables";
const STAFF_API = "https://schoolcrm-fzcv.onrender.com/api/staff";
export default function TimetablePage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [selectedStd, setSelectedStd] = useState("Class 1");
  const [selectedType, setSelectedType] = useState("regular");
  const [selectedSec, setSelectedSec] = useState("A");
  const [selectedGrp, setSelectedGrp] = useState("Bio Maths");
  const [periods, setPeriods] = useState(DEFAULT_PERIODS);
  const [timetables, setTimetables] = useState({});
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const currentClassInfo = selectedType === "hs" ? {
    key: `${selectedStd} ${selectedGrp}-${selectedSec}`,
    std: selectedStd,
    grp: selectedGrp,
    sec: selectedSec,
    type: "hs"
  } : {
    key: `${selectedStd}-${selectedSec}`,
    std: selectedStd,
    sec: selectedSec,
    type: "regular"
  };
  const currentKey = currentClassInfo.key;
  const currentTimetable = timetables[currentKey]?.timetable || emptyTimetable(periods);
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const map = {};
      data.forEach(d => {
        map[d.className] = {
          timetable: d.timetable,
          periods: d.periods || DEFAULT_PERIODS
        };
      });
      setTimetables(map);
    } catch {
      setTimetables({});
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch full staff records (name + dept + subjects) so the teacher
  // dropdown can be filtered down to only the staff who teach the
  // subject currently selected in the cell editor.
  const fetchStaff = useCallback(async () => {
    try {
      const res = await fetch(STAFF_API);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const list = (Array.isArray(data) ? data : []).map(s => ({
        id: s._id || s.id,
        name: (s.name || "").trim(),
        dept: (s.dept || "").trim(),
        subjects: Array.isArray(s.subjects) ? s.subjects : []
      })).filter(s => Boolean(s.name)).sort((a, b) => a.name.localeCompare(b.name));
      setStaffList(list);
    } catch {
      setStaffList([]);
    }
  }, []);
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);
  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);
  useEffect(() => {
    const classData = timetables[currentKey];
    if (classData?.periods?.length) {
      setPeriods(classData.periods);
    } else {
      setPeriods(DEFAULT_PERIODS);
    }
  }, [currentKey, timetables]);
  const handlePeriodHeaderSave = updated => {
    const updatedPeriods = periods.map(p => p.id === updated.id ? updated : p);
    setPeriods(updatedPeriods);
    setTimetables(prev => {
      const existing = prev[currentKey] || {
        timetable: emptyTimetable(updatedPeriods)
      };
      return {
        ...prev,
        [currentKey]: {
          ...existing,
          periods: updatedPeriods
        }
      };
    });
  };
  const handleCellSave = (day, periodId, value) => {
    if (value.teacher && value.teacher.trim()) {
      const hasConflict = isTeacherAllocated(value.teacher, day, periodId, timetables, currentKey);
      if (hasConflict) {
        alert(`Teacher "${value.teacher}" is already allocated to another class for this day and period. Please select a different teacher or period.`);
        return;
      }
    }
    setTimetables(prev => {
      const existing = prev[currentKey] || {
        timetable: emptyTimetable(periods),
        periods: periods
      };
      return {
        ...prev,
        [currentKey]: {
          ...existing,
          timetable: {
            ...existing.timetable,
            [day]: {
              ...(existing.timetable[day] || {}),
              [periodId]: value
            }
          }
        }
      };
    });
  };
  const handleCellDelete = (day, periodId) => {
    const p = periods.find(x => x.id === periodId);
    handleCellSave(day, periodId, {
      subject: "",
      teacher: "",
      duration: p?.duration || 40
    });
  };
  const handleSave = async () => {
    // Final safety-net: re-scan the WHOLE timetable being saved against
    // every other class, in case a conflict ever slipped past the
    // per-cell check (e.g. header/period edits, stale data, etc).
    // This guarantees a clash can never be silently written to the DB.
    const conflicts = findAllConflicts(currentTimetable, timetables, currentKey);
    if (conflicts.length > 0) {
      const lines = conflicts.map(c => `• ${c.day}, Period ${c.periodId}: "${c.teacher}" is already allocated to ${c.conflictClass}`).join("\n");
      const proceed = window.confirm(` Teacher allocation conflict(s) found:\n\n${lines}\n\n` + `These may be pre-existing entries from before this check was added. ` + `Click OK to save anyway, or Cancel to go back and fix them.`);
      if (!proceed) return;
    }
    setSaving(true);
    setSaveMsg("");
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          className: currentKey,
          timetable: currentTimetable,
          periods: periods
        })
      });
      if (!res.ok) throw new Error();
      setSaveMsg("Saved successfully!");
      setTimetables(prev => ({
        ...prev,
        [currentKey]: {
          timetable: currentTimetable,
          periods: periods
        }
      }));
    } catch {
      setSaveMsg("Save failed");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(""), 2500);
    }
  };
  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      await fetch(`${API}/${encodeURIComponent(currentKey)}`, {
        method: "DELETE"
      });
      setTimetables(prev => {
        const n = {
          ...prev
        };
        delete n[currentKey];
        return n;
      });
      setSaveMsg("Deleted successfully");
    } catch {
      setSaveMsg("Delete failed");
    } finally {
      setTimeout(() => setSaveMsg(""), 2000);
    }
  };
  const hasTimetable = !!timetables[currentKey];
  const activePeriods = periods.filter(p => !p.isBreak);
  const filledCount = hasTimetable ? Object.values(currentTimetable).reduce((acc, dayObj) => acc + Object.values(dayObj).filter(c => c.subject).length, 0) : 0;
  const totalSlots = DAYS.length * activePeriods.length;
  const totalMinutes = hasTimetable ? Object.values(currentTimetable).reduce((acc, dayObj) => acc + Object.values(dayObj).filter(c => c.subject).reduce((s, c) => s + (c.duration || 40), 0), 0) : 0;
  const totalHrs = Math.floor(totalMinutes / 60);
  const totalMin = totalMinutes % 60;

  // Sidebar content (shared between desktop & mobile overlay)
  const SidebarContent = () => <>
      <div style={{
      padding: "14px 14px 6px"
    }}>
        <div style={{
        fontSize: 10,
        fontWeight: 700,
        color: "#0D47A1",
        letterSpacing: "0.8px",
        textTransform: "uppercase",
        marginBottom: 8
      }}>LKG – Class 10</div>
        {REGULAR_STANDARDS.map(std => {
        const isActive = selectedStd === std && selectedType === "regular";
        return <button key={std} className={`std-btn${isActive ? " active" : ""}`} onClick={() => {
          setSelectedStd(std);
          setSelectedType("regular");
          setSelectedSec("A");
          setMobileMenuOpen(false);
        }} style={{
          width: "100%",
          textAlign: "left",
          padding: "8px 12px",
          borderRadius: 8,
          border: "none",
          fontSize: 12,
          fontWeight: isActive ? 700 : 400,
          color: isActive ? "#FFFFFF" : "#374151",
          background: isActive ? "#0D47A1" : "transparent",
          cursor: "pointer",
          marginBottom: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
              <span>{std}</span>
              <span style={{
            fontSize: 9,
            color: isActive ? "#90CAF9" : "#cbd5e1"
          }}>A B C</span>
            </button>;
      })}
      </div>

      <div style={{
      height: 1,
      background: "#f1f5f9",
      margin: "6px 14px"
    }} />

      <div style={{
      padding: "6px 14px 14px"
    }}>
        <div style={{
        fontSize: 10,
        fontWeight: 700,
        color: "#0D47A1",
        letterSpacing: "0.8px",
        textTransform: "uppercase",
        marginBottom: 8
      }}>Class 11 & 12</div>
        {HS_STANDARDS.map(std => <div key={std} style={{
        marginBottom: 6
      }}>
            <div style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#0D47A1",
          padding: "4px 12px",
          background: "#E3F2FD",
          borderRadius: 6,
          marginBottom: 4
        }}>{std}</div>
            {HS_GROUPS.map(grp => {
          const gc = HS_GROUP_COLORS[grp];
          const isActive = selectedType === "hs" && selectedStd === std && selectedGrp === grp;
          return <button key={grp} onClick={() => {
            setSelectedStd(std);
            setSelectedType("hs");
            setSelectedGrp(grp);
            setSelectedSec("A");
            setMobileMenuOpen(false);
          }} style={{
            width: "100%",
            textAlign: "left",
            padding: "7px 12px",
            borderRadius: 7,
            border: `1px solid ${isActive ? gc.border : "transparent"}`,
            fontSize: 11,
            fontWeight: isActive ? 700 : 400,
            color: isActive ? gc.color : "#64748b",
            background: isActive ? gc.bg : "transparent",
            cursor: "pointer",
            marginBottom: 2,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
                  <span style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: gc.dot,
              flexShrink: 0
            }} />
                  {grp}
                  <span style={{
              marginLeft: "auto",
              fontSize: 9,
              color: isActive ? gc.color : "#cbd5e1"
            }}>A B</span>
                </button>;
        })}
          </div>)}
      </div>
    </>;
  return <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;font-family:'Poppins',sans-serif;}
        body{background:#f1f5f9;}
        ::-webkit-scrollbar{width:5px;height:5px;}
        ::-webkit-scrollbar-track{background:#f1f5f9;}
        ::-webkit-scrollbar-thumb{background:#90CAF9;border-radius:3px;}
        .std-btn:hover:not(.active){background:#E3F2FD!important;}
        .std-btn.active{background:#0D47A1!important;color:#FFFFFF!important;}
        .nav-btn{transition:all 0.2s;}
        .nav-btn:hover{filter:brightness(1.15);transform:translateY(-1px);}
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        .fade-in{animation:fadeIn 0.22s ease;}
        td:hover .cell-del-btn{opacity:1!important;}
        .mobile-back-btn { display: none; }
        @media (max-width: 768px) {
          .mobile-back-btn { display: inline-flex !important; }
          .desktop-only-btn { display: none !important; }
        }
        @media (min-width: 769px) {
          .mobile-back-btn { display: none !important; }
          .desktop-only-btn { display: inline-flex !important; }
        }
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area {
            position: absolute; left: 0; top: 0; width: 100%;
            border: none !important; box-shadow: none !important;
          }
        }
      `}</style>

      <div style={{
      minHeight: "100vh",
      background: "#f1f5f9",
      display: "flex",
      flexDirection: "column"
    }}>

        {/* ── HEADER ── */}
        <div style={{
        background: "#0D47A1",
        padding: isMobile ? "10px 14px" : "12px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 100,
        borderBottom: "1px solid #2196F3",
        gap: 8,
        flexWrap: "wrap"
      }}>
          
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10
        }}>
            {/* Mobile Back Button */}
            <button className="mobile-back-btn" onClick={() => navigate("/admin")} style={{
            background: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "#FFFFFF",
            padding: "5px 10px",
            borderRadius: 8,
            fontSize: 13,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 5
          }} title="Back to Admin">
              <ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /> Back
            </button>
            
            <div>
              <div style={{
              fontWeight: 700,
              fontSize: isMobile ? 13 : 17,
              color: "#FFFFFF"
            }}>Timetable Management</div>
              {!isMobile && <div style={{
              fontSize: 10,
              color: "#90CAF9",
              marginTop: 2
            }}>
                  Assign & manage class-wise timetables · 2025-26
                </div>}
            </div>
          </div>

          <div style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          flexWrap: "wrap"
        }}>
            {saveMsg && <div style={{
            background: saveMsg.startsWith("Saved") ? "#dcfce7" : saveMsg.startsWith("Deleted") ? "#fef9c3" : "#fee2e2",
            color: saveMsg.startsWith("Saved") ? "#166534" : saveMsg.startsWith("Deleted") ? "#854d0e" : "#991b1b",
            padding: "4px 10px",
            borderRadius: 8,
            fontSize: 10,
            fontWeight: 600
          }}>{saveMsg}</div>}
            
            {/* Desktop Buttons */}
            <div className="desktop-only-btn" style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            flexWrap: "wrap"
          }}>
              <button className="nav-btn" onClick={() => navigate("/admin")} style={{
              background: "rgba(255,255,255,0.1)",
              color: "#90CAF9",
              border: "1px solid rgba(255,255,255,0.2)",
              padding: "6px 12px",
              borderRadius: 9,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer"
            }}>
                <ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /> Back
              </button>
            </div>
            
          </div>
        </div>

        <div style={{
        display: "flex",
        flex: 1,
        flexDirection: isMobile ? "column" : "row"
      }}>

          {/* ── MOBILE CLASS SELECTOR BUTTON ── */}
          {isMobile && <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{
          margin: "10px 12px 0",
          padding: "10px 14px",
          background: "#0D47A1",
          color: "#FFFFFF",
          border: "none",
          borderRadius: 10,
          fontSize: 12,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer"
        }}>
              <span>
                <ClipboardList className="inline w-[1em] h-[1em] align-[-0.125em]" /> {selectedType === "hs" ? `${selectedStd} · ${selectedGrp} · Sec ${selectedSec}` : `${selectedStd} · Sec ${selectedSec}`}
              </span>
              <span>{mobileMenuOpen ? "▲" : "▼"}</span>
            </button>}

          {/* ── MOBILE DROPDOWN OVERLAY ── */}
          {isMobile && mobileMenuOpen && <div style={{
          position: "fixed",
          top: 54,
          left: 10,
          right: 10,
          zIndex: 200,
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
          maxHeight: "70vh",
          overflowY: "auto",
          border: "1px solid #e2e8f0"
        }}>
              <div style={{
            padding: "12px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
                <span style={{
              fontWeight: 600,
              fontSize: 13
            }}>Select Class</span>
                <button onClick={() => setMobileMenuOpen(false)} style={{
              background: "none",
              border: "none",
              fontSize: 18,
              cursor: "pointer"
            }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
              </div>
              <SidebarContent />
            </div>}

          {/* ── DESKTOP SIDEBAR ── */}
          {!isMobile && <div style={{
          width: 250,
          background: "#fff",
          borderRight: "1px solid #e2e8f0",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          overflowY: "auto"
        }}>
              <SidebarContent />
            </div>}

          {/* ── MAIN CONTENT ── */}
          <div style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column"
        }}>

            {/* Sub-header: class info + section + save */}
            <div style={{
            background: "#fff",
            borderBottom: "1px solid #e2e8f0",
            padding: isMobile ? "10px 12px" : "12px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
            flexShrink: 0
          }}>
              <div style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              flex: 1,
              minWidth: 0
            }}>
                <div style={{
                minWidth: 0
              }}>
                  <div style={{
                  fontWeight: 700,
                  fontSize: isMobile ? 12 : 15,
                  color: "#0f172a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}>
                    {selectedType === "hs" ? <span style={{
                    color: (HS_GROUP_COLORS[selectedGrp] || {}).color
                  }}>{selectedStd} · {selectedGrp}</span> : selectedStd}
                  </div>
                  <div style={{
                  fontSize: 9,
                  color: "#64748b",
                  marginTop: 2
                }}>
                    Sec {selectedSec} · {filledCount}/{totalSlots} filled
                    {totalMinutes > 0 && <span style={{
                    marginLeft: 6,
                    color: "#0D47A1",
                    fontWeight: 600
                  }}>
                        · ⏱ {totalHrs > 0 ? `${totalHrs}h ` : ""}{totalMin > 0 ? `${totalMin}m` : ""}/wk
                      </span>}
                  </div>
                </div>
                <div style={{
                display: "flex",
                gap: 5,
                flexShrink: 0
              }}>
                  {(selectedType === "hs" ? HS_SECTIONS : REGULAR_SECTIONS).map(sec => <button key={sec} onClick={() => setSelectedSec(sec)} style={{
                  padding: isMobile ? "3px 10px" : "4px 12px",
                  borderRadius: 20,
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: "pointer",
                  border: selectedSec === sec ? "none" : "1px solid #e2e8f0",
                  background: selectedSec === sec ? "#0D47A1" : "transparent",
                  color: selectedSec === sec ? "#FFFFFF" : "#64748b"
                }}>
                      {sec}
                    </button>)}
                </div>
                {selectedType === "hs" && !isMobile && <div style={{
                background: HS_GROUP_COLORS[selectedGrp]?.bg,
                border: `1px solid ${HS_GROUP_COLORS[selectedGrp]?.border}`,
                color: HS_GROUP_COLORS[selectedGrp]?.color,
                borderRadius: 6,
                padding: "3px 8px",
                fontSize: 10,
                fontWeight: 600
              }}>
                    <BookOpen className="inline w-[1em] h-[1em] align-[-0.125em]" /> {selectedGrp}
                  </div>}
              </div>
              <div style={{
              display: "flex",
              gap: 6,
              flexShrink: 0
            }}>
                <button onClick={() => window.print()} style={{
                background: "#e0f2fe",
                color: "#075985",
                border: "1px solid #bae6fd",
                padding: isMobile ? "5px 10px" : "6px 12px",
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 600,
                cursor: "pointer"
              }}>
                  <Printer className="inline w-[1em] h-[1em] align-[-0.125em]" /> Print
                </button>
                {hasTimetable && <button onClick={() => setShowDeleteConfirm(true)} style={{
                background: "#fee2e2",
                color: "#991b1b",
                border: "1px solid #fecaca",
                padding: isMobile ? "5px 10px" : "6px 12px",
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 600,
                cursor: "pointer"
              }}>
                    Delete
                  </button>}
                <button onClick={handleSave} disabled={saving} style={{
                background: saving ? "#2196F3" : "#0D47A1",
                color: "#FFFFFF",
                border: "none",
                padding: isMobile ? "5px 12px" : "6px 16px",
                borderRadius: 8,
                fontSize: isMobile ? 11 : 12,
                fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer"
              }}>
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>

            {/* Progress bar */}
            <div style={{
            height: 3,
            background: "#e2e8f0",
            flexShrink: 0
          }}>
              <div style={{
              height: 3,
              width: `${totalSlots ? filledCount / totalSlots * 100 : 0}%`,
              background: "linear-gradient(90deg,#0D47A1,#2196F3)",
              transition: "width 0.4s"
            }} />
            </div>

            {/* Content */}
            <div className="fade-in" style={{
            flex: 1,
            padding: isMobile ? "10px 10px" : "16px 20px"
          }}>
              {loading ? <div style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200
            }}>
                  <div style={{
                color: "#94a3b8",
                fontSize: 13
              }}>Loading timetables...</div>
                </div> : <>
                  {/* Tip */}
                  <div style={{
                marginBottom: 10,
                padding: "7px 12px",
                background: "#E3F2FD",
                border: "1px solid #90CAF9",
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap"
              }}>
                    <span style={{
                  fontSize: 12
                }}><Lightbulb className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                    <span style={{
                  fontSize: 9,
                  color: "#0D47A1"
                }}>
                      <strong>Tap column headers</strong> to edit period info.&nbsp;
                      <strong>Tap cells</strong> to add/edit.&nbsp;
                      {!isMobile && <><strong>Hover & <X className="inline w-[1em] h-[1em] align-[-0.125em]" /></strong> to delete.</>}
                    </span>
                  </div>

                  {/* Grid */}
                  <div className="print-area" style={{
                background: "#fff",
                borderRadius: 14,
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.05)"
              }}>
                    <TimetableGrid key={currentKey} classInfo={currentClassInfo} timetable={currentTimetable} periods={periods} staffList={staffList} allTimetables={timetables} currentKey={currentKey} onCellSave={handleCellSave} onCellDelete={handleCellDelete} onPeriodHeaderSave={handlePeriodHeaderSave} />
                  </div>

                  {/* Legend */}
                  <div style={{
                marginTop: 12,
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                alignItems: "center"
              }}>
                    <span style={{
                  fontSize: 9,
                  color: "#94a3b8",
                  fontWeight: 500
                }}>Legend:</span>
                    {[{
                  label: "Tamil",
                  bg: "#E3F2FD",
                  color: "#0D47A1"
                }, {
                  label: "English",
                  bg: "#E3F2FD",
                  color: "#0D47A1"
                }, {
                  label: "Mathematics",
                  bg: "#E3F2FD",
                  color: "#0D47A1"
                }, {
                  label: "Science",
                  bg: "#E3F2FD",
                  color: "#0D47A1"
                }, {
                  label: "Social Science",
                  bg: "#E3F2FD",
                  color: "#0D47A1"
                }, {
                  label: "Free Period",
                  bg: "#E3F2FD",
                  color: "#0D47A1"
                }].map(l => <span key={l.label} style={{
                  padding: "2px 7px",
                  borderRadius: 5,
                  fontSize: 9,
                  fontWeight: 600,
                  background: l.bg,
                  color: l.color
                }}>
                        {l.label}
                      </span>)}
                  </div>

                  {/* Stats */}
                  <div style={{
                marginTop: 14,
                display: "grid",
                gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)",
                gap: isMobile ? 8 : 12
              }}>
                    {[{
                  label: "Periods Filled",
                  value: filledCount,
                  color: "#0D47A1",
                  bg: "#E3F2FD",
                  icon: <ClipboardList className="inline w-[1em] h-[1em] align-[-0.125em]" />
                }, {
                  label: "Periods Empty",
                  value: totalSlots - filledCount,
                  color: "#2196F3",
                  bg: "#E3F2FD",
                  icon: <Square className="inline w-[1em] h-[1em] align-[-0.125em]" />
                }, {
                  label: "Total Slots",
                  value: totalSlots,
                  color: "#0D47A1",
                  bg: "#E3F2FD",
                  icon: <BarChart3 className="inline w-[1em] h-[1em] align-[-0.125em]" />
                }, {
                  label: "Weekly Duration",
                  value: totalMinutes > 0 ? `${totalHrs}h ${totalMin}m` : "—",
                  color: "#0D47A1",
                  bg: "#E3F2FD",
                  icon: "⏱"
                }].map(s => <div key={s.label} style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  padding: isMobile ? "8px 10px" : "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}>
                        <div style={{
                    width: isMobile ? 32 : 38,
                    height: isMobile ? 32 : 38,
                    borderRadius: 10,
                    background: s.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: typeof s.value === "string" ? 9 : 13,
                    color: s.color,
                    flexShrink: 0
                  }}>
                          {s.value}
                        </div>
                        <div>
                          <div style={{
                      fontSize: 8,
                      color: "#94a3b8"
                    }}>{s.icon}</div>
                          <div style={{
                      fontSize: 9,
                      color: "#64748b",
                      fontWeight: 500
                    }}>{s.label}</div>
                        </div>
                      </div>)}
                  </div>
                </>}
            </div>
          </div>
        </div>
      </div>

      {showDeleteConfirm && <ConfirmDialog message={`Delete entire timetable for "${currentKey}"? Cannot be undone.`} onConfirm={handleDelete} onCancel={() => setShowDeleteConfirm(false)} />}
    </>;
}